import { useEffect, useState } from "react";
import { Info, Loader2, Repeat, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ComboSelect } from "@/components/common/ComboSelect";
import { api, apiError, listResource } from "@/lib/api";
import { toast } from "sonner";

import { labelTipoPersona } from "@/lib/catalogos_maestros";

// Selector de origen del cliente en "Nueva solicitud": 2 modos.
//  1) Cliente existente · 2) Desde prospecto (convierte prospecto ya capturado en forma)
// El alta express fue eliminada (feb-2026, decisión PO): el motor de riesgo PLD vive en el
// PROSPECTO. Toda solicitud se origina sobre un cliente existente o convirtiendo un
// prospecto ya capturado con producto_interes, destino_recurso y perfil transaccional.
export function OrigenClienteSelect({ form, onCliente }) {
  const [modo, setModo] = useState("existente");
  const [clientes, setClientes] = useState([]);
  const [prospectos, setProspectos] = useState([]);
  const [prospSel, setProspSel] = useState(null);
  const [busy, setBusy] = useState(false);

  const cargar = () => {
    listResource("clientes").then(setClientes).catch(() => {});
    // sep-2026 · MULTICRÉDITO — los prospectos YA CONVERTIDOS siguen en la lista (un cliente
    // puede tener varios créditos). Se marcan «Ya es cliente» y al usarlos se reutiliza su
    // expediente en vez de convertirlos otra vez.
    listResource("prospectos").then(setProspectos).catch(() => {});
  };
  useEffect(() => { cargar(); }, []);

  const yaEsCliente = (p) => !!p && (p.estatus === "convertido" || !!p.cliente_id_convertido);

  const usarConvertido = (cliente) => {
    onCliente(cliente);
    setClientes((prev) => (prev.some((c) => c.id === cliente.id) ? prev : [cliente, ...prev]));
    toast.success(`Cliente listo: ${cliente.nombre || cliente.razon_social}`);
  };

  const convertirProspecto = async () => {
    if (!prospSel) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/prospectos/${prospSel.id}/convertir`, {});
      if (data.ya_es_cliente) toast.info(data.mensaje || "Este prospecto ya es cliente; se usará su expediente.");
      usarConvertido(data.cliente);
      cargar();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const AsignacionAutomatica = () => (
    <div data-testid="asignacion-automatica-nota" className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
      <Users className="mt-0.5 h-3.5 w-3.5 shrink-0 text-forest-600" />
      <p className="text-[11px] text-slate-500">
        <span className="font-medium text-slate-600">Asignación automática (round-robin).</span> El cliente se reparte de forma equilibrada entre el equipo de cobranza de la entidad. Si no hay equipo, queda sin asignar en la bandeja del administrador.
      </p>
    </div>
  );

  return (
    <div className="space-y-3" data-testid="origen-cliente-select">
      <div className="grid grid-cols-2 gap-2">
        {[["existente", "Cliente existente", Users], ["prospecto", "Desde prospecto", Repeat]].map(([val, lbl, Icon]) => (
          <button key={val} type="button" data-testid={`origen-modo-${val}`}
            onClick={() => setModo(val)}
            className={`flex items-center justify-center gap-1.5 rounded-md border px-2 py-2 text-xs font-medium transition-colors ${modo === val ? "border-accent bg-forest-600/10 text-forest-700" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
            <Icon className="h-3.5 w-3.5" /> {lbl}
          </button>
        ))}
      </div>

      {form?.cliente_id && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs text-emerald-800" data-testid="origen-cliente-elegido">
          Cliente seleccionado: <strong>{form.cliente}</strong>
        </div>
      )}

      {modo === "existente" && (
        <ComboSelect testid="origen-combo-cliente" items={clientes} value={form?.cliente_id ?? ""}
          fallbackLabel={form?.cliente || ""} onSelect={(c) => onCliente(c)}
          getLabel={(c) => c.nombre || c.razon_social}
          getSub={(c) => [c.rfc, labelTipoPersona(c.tipo_persona), c.expediente_estatus].filter(Boolean).join(" · ")}
          getSearch={(c) => `${c.nombre || ""} ${c.rfc || ""}`}
          placeholder="Selecciona un cliente..." searchPlaceholder="Buscar por nombre o RFC..."
          emptyText="No hay clientes. Usa 'Desde prospecto' para convertir uno ya capturado." />
      )}

      {modo === "prospecto" && (
        <div className="space-y-2">
          <ComboSelect testid="origen-combo-prospecto" items={prospectos} value={prospSel?.id ?? ""}
            onSelect={(p) => setProspSel(p)}
            getLabel={(p) => p.nombre || p.razon_social}
            getSub={(p) => [p.rfc, p.expediente_estatus, yaEsCliente(p) ? "Ya es cliente" : p.estatus].filter(Boolean).join(" · ")}
            getSearch={(p) => `${p.nombre || ""} ${p.rfc || ""}`}
            placeholder="Selecciona un prospecto..." searchPlaceholder="Buscar prospecto..."
            emptyText="No hay prospectos capturados." />
          {yaEsCliente(prospSel) ? (
            <div data-testid="origen-prospecto-ya-cliente"
                 className="flex items-start gap-2 rounded-md border border-sky-200 bg-sky-50 px-3 py-2">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-sky-600" />
              <p className="text-[11px] text-sky-900">
                <span className="font-medium">Este prospecto ya es cliente; se usará su expediente.</span>{" "}
                No se crea un cliente nuevo: el segundo crédito reutiliza el expediente, el screening
                y la autorización SIC vigente que ya tiene.
              </p>
            </div>
          ) : <AsignacionAutomatica />}
          <Button type="button" size="sm" data-testid="origen-convertir-btn" disabled={!prospSel || busy} onClick={convertirProspecto} className="bg-accent hover:bg-forest-700">
            {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Repeat className="mr-1.5 h-4 w-4" />}
            {yaEsCliente(prospSel) ? "Usar cliente existente" : "Convertir a cliente y usar"}
          </Button>
          <p className="text-xs text-slate-400">La conversión hereda el expediente y el screening del prospecto (sin recapturar ni duplicar alertas).</p>
        </div>
      )}
    </div>
  );
}
